import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  color: #333;
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;
`;

const HeaderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
`;

const Logo = styled.div`
  h1 {
    font-size: 1.5rem;
    color: #632ce4;
    font-weight: 700;
    margin: 0;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #333;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease;
  position: relative;
  cursor: pointer;

  &:hover {
    color: #632ce4;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(135deg, #632ce4, #9d50bb);
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const LoginButton = styled.button`
  padding: 0.625rem 1.5rem;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(99, 44, 228, 0.4);
  }
`;

const Hero = styled.section`
  padding: 10rem 0 6rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ebf0 100%);
  margin-top: 70px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 600px;
    height: 600px;
    background: linear-gradient(135deg, #632ce4, #9d50bb);
    opacity: 0.1;
    border-radius: 50%;
    filter: blur(80px);
  }

  @media (max-width: 768px) {
    padding: 6rem 0 4rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  color: #1a1d24;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 1024px) {
    font-size: 2.75rem;
  }

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.875rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.35rem;
  color: #666;
  margin-bottom: 2.5rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Button = styled.button`
  padding: 1.125rem 2.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #632ce4, #9d50bb);
    color: #fff;
    box-shadow: 0 4px 15px rgba(99, 44, 228, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(99, 44, 228, 0.5);
    }
  ` : `
    background: transparent;
    color: #632ce4;
    border: 2px solid #632ce4;

    &:hover {
      background: #632ce4;
      color: #fff;
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  background: ${props => props.$gradient ? 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)' : '#fff'};

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  direction: ${props => props.$reverse ? 'rtl' : 'ltr'};

  > * {
    direction: ltr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    direction: ltr;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1d24;
  margin-bottom: 1.5rem;
  line-height: 1.3;
  text-align: ${props => props.$center ? 'center' : 'left'};

  @media (max-width: 1024px) {
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const SectionText = styled.p`
  font-size: 1.125rem;
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.8;
  text-align: ${props => props.$center ? 'center' : 'left'};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ImagePlaceholder = styled.div`
  background: linear-gradient(135deg, #e8ebf0, #f5f7fa);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  aspect-ratio: 4/3;
  display: flex;
  align-items: center;
  justify-content: center;

  @supports not (aspect-ratio: 4/3) {
    &::before {
      content: '';
      float: left;
      padding-top: 75%;
    }
    &::after {
      content: '';
      display: block;
      clear: both;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageCaption = styled.p`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: monospace;
  margin: 0;
`;

const FeatureBadges = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const Badge = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
  }
`;

const BadgeIcon = styled.span`
  font-size: 2rem;
  flex-shrink: 0;
`;

const BadgeContent = styled.div`
  h4 {
    color: #1a1d24;
    margin-bottom: 0.25rem;
    font-size: 1.1rem;
  }

  p {
    color: #666;
    font-size: 0.95rem;
    margin: 0;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

const FeatureItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.3);
`;

const FeatureContent = styled.div`
  h3 {
    color: #1a1d24;
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
  }

  p {
    color: #666;
    line-height: 1.7;
    margin: 0;
  }
`;

const PricingCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.1);
  position: relative;
  border: 3px solid #632ce4;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const PricingBadge = styled.div`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  color: #fff;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.4);
`;

const PricingTitle = styled.h3`
  text-align: center;
  font-size: 1.75rem;
  color: #1a1d24;
  margin-bottom: 0.5rem;
`;

const PricingSubtitle = styled.p`
  text-align: center;
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

const PricingPrice = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const PriceOld = styled.span`
  font-size: 2rem;
  color: #888;
  text-decoration: line-through;
`;

const PriceCurrent = styled.span`
  font-size: 4rem;
  font-weight: 800;
  color: #632ce4;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PricePeriod = styled.span`
  font-size: 1.25rem;
  color: #666;
`;

const PricingImage = styled.div`
  margin: 2rem 0;

  ${ImagePlaceholder} {
    aspect-ratio: 16/9;

    @supports not (aspect-ratio: 16/9) {
      &::before {
        padding-top: 56.25%;
      }
    }
  }
`;

const PricingFeatures = styled.ul`
  list-style: none;
  margin: 2rem 0;
  padding: 0;
`;

const FeatureListItem = styled.li`
  padding: 1rem 0;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const Check = styled.span`
  color: #27ae60;
  font-size: 1.25rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const PricingNote = styled.p`
  text-align: center;
  color: #888;
  font-size: 0.875rem;
  margin-top: 1.5rem;
  font-style: italic;
`;

const CTASection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  text-align: center;
  color: #fff;
`;

const CTATitle = styled.h2`
  font-size: 2.75rem;
  font-weight: 700;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2.5rem;
  opacity: 0.9;
`;

const CTAButton = styled(Button)`
  background: #fff;
  color: #632ce4;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(255, 255, 255, 0.3);
  }
`;

const Footer = styled.footer`
  background: #1a1d24;
  color: #fff;
  padding: 4rem 0 2rem;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterSection = styled.div`
  h3, h4 {
    margin-bottom: 1rem;
    color: #fff;
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.8;
  }

  a {
    display: block;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
    text-decoration: none;
    transition: color 0.3s ease;
    cursor: pointer;

    &:hover {
      color: #fff;
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
`;

const LandingPage = ({ onLogin }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <PageContainer>
      <Header>
        <HeaderContainer>
          <Logo>
            <h1>📦 Famibox</h1>
          </Logo>
          <Nav>
            <NavLink onClick={() => scrollToSection('service')}>Le service</NavLink>
            <NavLink onClick={() => scrollToSection('features')}>Fonctionnalités</NavLink>
            <NavLink onClick={() => scrollToSection('pricing')}>Tarifs</NavLink>
            <LoginButton onClick={onLogin}>Se connecter</LoginButton>
          </Nav>
        </HeaderContainer>
      </Header>

      <Hero>
        <Container>
          <HeroContent>
            <HeroTitle>Gardez le lien avec vos proches âgés</HeroTitle>
            <HeroSubtitle>
              Famibox transforme votre téléviseur en fenêtre ouverte sur votre famille. 
              Simple, sécurisé et toujours à portée de télécommande.
            </HeroSubtitle>
            <HeroButtons>
              <Button $primary onClick={onLogin}>Accéder à l'application</Button>
              <Button onClick={() => scrollToSection('service')}>En savoir plus</Button>
            </HeroButtons>
          </HeroContent>
        </Container>
      </Hero>

      <Section id="service">
        <Container>
          <SectionGrid>
            <div>
              <SectionTitle>Une vraie présence, pas juste un appel</SectionTitle>
              <SectionText>
                Famibox n'est pas simplement une application de visioconférence. C'est une expérience 
                unique qui transforme votre télévision en portail familial.
              </SectionText>
              <SectionText>
                <strong>Branché sur la télé,</strong> Famibox offre un confort incomparable par rapport 
                aux services classiques comme WhatsApp ou FaceTime. Plus besoin de tenir un petit écran 
                pendant des heures ou de se pencher sur une tablette.
              </SectionText>
              <SectionText>
                Installez-vous confortablement dans votre fauteuil et <strong>vivez les moments en famille</strong> : 
                discutez avec vos enfants tout en voyant vos petits-enfants jouer dans le salon, 
                partagez le repas du dimanche, assistez aux devoirs... <strong>comme si vous étiez là.</strong>
              </SectionText>
              <FeatureBadges>
                <Badge>
                  <BadgeIcon>🔒</BadgeIcon>
                  <BadgeContent>
                    <h4>Appels 100% sécurisés</h4>
                    <p>Connexion directe P2P, aucun serveur tiers</p>
                  </BadgeContent>
                </Badge>
                <Badge>
                  <BadgeIcon>🎥</BadgeIcon>
                  <BadgeContent>
                    <h4>Qualité 4K</h4>
                    <p>Image cristalline sur grand écran</p>
                  </BadgeContent>
                </Badge>
                <Badge>
                  <BadgeIcon>📺</BadgeIcon>
                  <BadgeContent>
                    <h4>Sur votre télé</h4>
                    <p>Navigation à la télécommande</p>
                  </BadgeContent>
                </Badge>
              </FeatureBadges>
            </div>
            <div>
              <ImagePlaceholder>
                <img src="/images/family-call.jpg" alt="Appel en famille sur Famibox" />
              </ImagePlaceholder>
            </div>
          </SectionGrid>
        </Container>
      </Section>

      <Section id="features" $gradient>
        <Container>
          <SectionGrid $reverse>
            <div>
              <ImagePlaceholder>
                <img src="/images/fall-detection.jpg" alt="Détection de chute Famibox" />
              </ImagePlaceholder>
            </div>
            <div>
              <SectionTitle>Veiller sur vos proches en toute discrétion</SectionTitle>
              <SectionText>
                Au-delà des appels vidéo, Famibox intègre des technologies de pointe pour 
                assurer la sécurité de vos proches.
              </SectionText>
              
              <FeatureList>
                <FeatureItem>
                  <FeatureIcon>🚨</FeatureIcon>
                  <FeatureContent>
                    <h3>Détection de chute intelligente</h3>
                    <p>
                      Grâce à l'intelligence artificielle, Famibox surveille discrètement 
                      les mouvements et détecte automatiquement les chutes. En cas d'incident, 
                      un appel d'urgence est déclenché instantanément vers les contacts enregistrés 
                      et les services de secours si nécessaire.
                    </p>
                  </FeatureContent>
                </FeatureItem>
                
                <FeatureItem>
                  <FeatureIcon>📅</FeatureIcon>
                  <FeatureContent>
                    <h3>Suivi des rendez-vous médicaux</h3>
                    <p>
                      Ne manquez plus jamais un rendez-vous important. Famibox vous rappelle 
                      automatiquement vos consultations médicales, prises de médicaments et 
                      autres événements importants directement sur votre écran de télévision.
                    </p>
                  </FeatureContent>
                </FeatureItem>
                
                <FeatureItem>
                  <FeatureIcon>📸</FeatureIcon>
                  <FeatureContent>
                    <h3>Partage de photos et vidéos</h3>
                    <p>
                      Recevez instantanément les photos et vidéos de votre famille sur votre 
                      télévision. Revivez les moments précieux en grand format, comme si vous 
                      y étiez.
                    </p>
                  </FeatureContent>
                </FeatureItem>
              </FeatureList>
            </div>
          </SectionGrid>
        </Container>
      </Section>

      <Section id="pricing">
        <Container>
          <div style={{ maxWidth: '700px', margin: '0 auto 4rem', textAlign: 'center' }}>
            <SectionTitle $center>Une offre tout compris</SectionTitle>
            <SectionText $center>
              Tout ce dont vous avez besoin pour rester connecté avec vos proches
            </SectionText>
          </div>

          <PricingCard>
            <PricingTitle>Formule Complète</PricingTitle>
            <PricingSubtitle>sans engagement</PricingSubtitle>
            <PricingPrice>
              <PriceCurrent>29€99</PriceCurrent>
              <PricePeriod>/ mois*</PricePeriod>
            </PricingPrice>
            
            <PricingImage>
              <ImagePlaceholder>
                <img src="/images/famibox-device.jpg" alt="Boîtier Famibox" />
              </ImagePlaceholder>
            </PricingImage>
            
            <PricingFeatures>
              <FeatureListItem>
                <Check>✓</Check>
                <strong>Le boîtier avec caméra 4K</strong>
              </FeatureListItem>
              <FeatureListItem>
                <Check>✓</Check>
                <strong>128 Go de stockage</strong> pour vos photos et vidéos
              </FeatureListItem>
              <FeatureListItem>
                <Check>✓</Check>
                <strong>Appels vidéo illimités</strong> en qualité HD/4K
              </FeatureListItem>
              <FeatureListItem>
                <Check>✓</Check>
                <strong>Partage de photos</strong> instantané avec vos proches
              </FeatureListItem>
              <FeatureListItem>
                <Check>✓</Check>
                <strong>Détection de chute</strong> par intelligence artificielle
              </FeatureListItem>
              <FeatureListItem>
                <Check>✓</Check>
                <strong>Suivi du patient</strong> (rendez-vous, rappels médicaux)
              </FeatureListItem>
              <FeatureListItem>
                <Check>✓</Check>
                <strong>Support technique</strong> dédié
              </FeatureListItem>
            </PricingFeatures>
            
            <Button $primary style={{ width: '100%' }} onClick={onLogin}>
              Commencer maintenant
            </Button>

            <PricingNote>
              * Une caution de 100€ vous sera aussi demandée pour couvrir le matériel.
              Elle vous sera intégralement remboursée lorsque vous nous retournerez le boîtier Famibox.
            </PricingNote>
          </PricingCard>
        </Container>
      </Section>

      <CTASection>
        <Container>
          <CTATitle>Prêt à rapprocher votre famille ?</CTATitle>
          <CTAText>
            Rejoignez les familles qui ont déjà choisi Famibox pour rester connectées
          </CTAText>
          <CTAButton onClick={onLogin}>
            Accéder à l'application
          </CTAButton>
        </Container>
      </CTASection>

      <Footer>
        <Container>
          <FooterContent>
            <FooterSection>
              <h3>📦 Famibox</h3>
              <p>Gardez le lien avec vos proches, simplement.</p>
            </FooterSection>
            <FooterSection>
              <h4>Liens rapides</h4>
              <a onClick={() => scrollToSection('service')}>Le service</a>
              <a onClick={() => scrollToSection('features')}>Fonctionnalités</a>
              <a onClick={() => scrollToSection('pricing')}>Tarifs</a>
              <a onClick={onLogin}>Se connecter</a>
            </FooterSection>
            <FooterSection>
              <h4>Contact</h4>
              <p>support@famibox.xxxxx.fr</p>
              <p>06.55.66.77.88</p>
              <p>Du lundi au vendredi<br />9h - 18h</p>
            </FooterSection>
          </FooterContent>
          <FooterBottom>
            <p>&copy; 2025 Famibox. Tous droits réservés.</p>
          </FooterBottom>
        </Container>
      </Footer>
    </PageContainer>
  );
};

export default LandingPage;